import { z } from "zod";
import { apiListSchema } from "./list";
import { HotelDtoSchema } from "./hotels";

//? DTO
export const CreateHotelRoomDtoSchema = z.object({
  Year: z.number().nullish(),
  Designation: z.string().nullish(),
  Count: z.number().nullish(),
  HotelId: z.number().nullish(),
  Notes: z.string().nullish(),
  Pricing: z.string().nullish(), //! This will host all logic of prices

  BookingDiscountMap: z.string().nullish(), //TODO Maybe delete
  Type: z.number().nullish(), // TODO Maybe delete
  ExtraBeds: z.number().nullish(), //TODO Maybe delete
  AverageExtraBedRateMap: z.string().nullish(), //TODO Maybe delete
  EarlyBookingDiscountDays: z.number().nullish(), //TODO Maybe delete
});
export type CreateHotelRoomDto = z.infer<typeof CreateHotelRoomDtoSchema>;

export const HotelRoomDtoSchema = CreateHotelRoomDtoSchema.extend({
  Id: z.number(),
  Hotel: HotelDtoSchema.nullish(),
});
export type HotelRoomDto = z.infer<typeof HotelRoomDtoSchema>;

export const hotelRoomDtoListSchema = apiListSchema(HotelRoomDtoSchema);
//? Model

export enum HotelRoomType {
  Twin = "Twin",
  Double = "Double",
  DSU = "DSU",
  Triple = "Triple",
  Other = "Other",
}

export const hotelRoomTypeEnumToNumber: Record<HotelRoomType, number> = {
  Twin: 0,
  Double: 1,
  DSU: 2,
  Triple: 3,
  Other: 4,
};

export const hotelRoomTypeNumToEnum = Object.entries(
  hotelRoomTypeEnumToNumber
).reduce((acc, [key, value]) => ({ ...acc, [value]: key }), {}) as Record<
  number,
  HotelRoomType
>;

export const HotelRoomTypesAsArray = Object.values(HotelRoomType);

export const CreateHotelRoomSchema = z.object({
  year: z.number().nullish(),
  type: z.nativeEnum(HotelRoomType).nullish(),
  designation: z.string().nullish(),
  count: z.number().nullish(),
  hotelId: z.number().nullish(),
  extraBeds: z.number().nullish(),
  averageExtraBedRateMap: z.record(z.any()).nullish(),
  notes: z.string().nullish(),
  pricing: z
    .array(
      z.object({
        type: z.string(),
        notes: z.string().nullish(),
        prices: z.record(z.any()).nullish(),
      })
    )
    .nullish(),
  earlyBookingDiscountDays: z.number().nullish(),
  bookingDiscountMap: z.record(z.any()).nullish(),
});

export const HotelRoomSchema = CreateHotelRoomSchema.extend({
  id: z.string().min(1),
  hotelName: z.string().nullish(),
});

export type CreateHotelRoom = z.infer<typeof CreateHotelRoomSchema>;

export type HotelRoom = z.infer<typeof HotelRoomSchema>;

//? Mappers

export function hotelRoomFromDto(dto: HotelRoomDto): HotelRoom {
  return {
    id: dto.Id.toString(),
    year: dto.Year ?? new Date().getFullYear(),
    type:
      typeof dto.Type === "number" ? hotelRoomTypeNumToEnum[dto.Type] : null,
    designation: dto.Designation ?? "",
    count: dto.Count,
    hotelId: dto.HotelId,
    extraBeds: dto.ExtraBeds,
    averageExtraBedRateMap: dto.AverageExtraBedRateMap
      ? JSON.parse(dto.AverageExtraBedRateMap)
      : {},
    notes: dto.Notes,
    pricing: dto.Pricing ? JSON.parse(dto.Pricing) : {},
    earlyBookingDiscountDays: dto.EarlyBookingDiscountDays,
    bookingDiscountMap:
      dto.BookingDiscountMap && dto.BookingDiscountMap.length
        ? JSON.parse(dto.BookingDiscountMap)
        : null,
    hotelName: dto.Hotel?.Name ?? "",
  };
}

export function hotelRoomToDto(dto: HotelRoom): HotelRoomDto {
  return {
    Id: Number.parseInt(dto.id),
    ...createHotelRoomToDto(dto),
  };
}

export function createHotelRoomToDto(dto: CreateHotelRoom): CreateHotelRoomDto {
  return {
    Year: dto.year,
    Type: dto.type ? hotelRoomTypeEnumToNumber[dto.type] : null,
    Designation: dto.designation,
    Count: dto.count,
    HotelId: dto.hotelId,
    ExtraBeds: dto.extraBeds,
    AverageExtraBedRateMap: JSON.stringify(dto.averageExtraBedRateMap),
    Notes: dto.notes,
    Pricing: JSON.stringify(dto.pricing),
    BookingDiscountMap: dto.bookingDiscountMap
      ? JSON.stringify(dto.bookingDiscountMap)
      : null,
    EarlyBookingDiscountDays: dto.earlyBookingDiscountDays,
  };
}
